/*
 *  Copyright (c) Qwoach LLC 2022. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 1/2/2022
 */

import {WorkflowItem} from "@shared/Workflow/WorkflowItem-imports.js";
import WorkflowStageItem from "@shared/Workflow/base/WorkflowStageItem";
import EmailReminderItem from "@shared/scheduler/reminders/EmailReminderItem";
import ReminderTriggerItemNow from "@shared/scheduler/reminders/ReminderTriggerItemNow";
import {WorkflowStateEnum} from "@shared/Workflow/WorkflowStateEnum";
import WorkflowDocRefItem from "@shared/Workflow/WorkflowDocRefItem";
import {CompletionStatus} from "@shared/Workflow/CompletionStatus";
import {TraumaWorkflowStateItem} from "@shared/Workflow/WorkflowStateItem-imports";
import {WorkflowDocumentType} from "@shared/Workflow/WorkflowDocumentType";
import {DbObjectType} from "@shared/Workflow/DbObjectType";
import TagItem from "@shared/Tags/TagItem";
import {ClearanceLevelEnum} from "@shared/Profile/ClearanceLevelEnum";
import {Type} from "@qwoach/class-transformer-wrapper";

const TraumaCoachingWorkflowItemIdInternal = "CbCaXgNEmoNqYa677cq0"


export default class TraumaCoachingWorkflowItem extends WorkflowItem {

	type: string = "TraumaCoachingWorkflowItem"

	needsSetup: boolean = true
	isActivated: boolean = false

	coachId: string = null

	title: string = null
	titleShort: string = null



	emailBriefIntakeForm: boolean = false
	emailDailyActivitiesForm: boolean = false

	safetyRiskAssessmentId: string = null
	activitiesOfDailyLivingId: string = null

	briefIntakeFormId: string = null
	detailedIntakeFormId: string = null

	crisisManagementPlanId: string = null
	clientSafetyPlanId: string = null

	recoveryGoalsWorksheetId: string = null

	sessionIdsForTrigger: string[] = []

	@Type(() => EmailReminderItem)
	emailReminders: EmailReminderItem[] = []

	clearanceLevel: ClearanceLevelEnum = ClearanceLevelEnum.PHI


	constructor() {
		super();
		this.id = TraumaCoachingWorkflowItemIdInternal
		this.title = "International Association of Trauma Recovery Coaching Workflow"//"Trauma Recovery Workflow"
		this.titleShort = "IAOTRC Workflow"

		// let res = visualize(this._stateMachine, { orientation: 'horizontal' })
		// console.log("StateMachine visualize = ", res)
		// console.log("StateMachine this.stateMachine = ", this._stateMachine)
		// console.log("StateMachine this.stateMachine = ", this._stateMachine.transitions())
		// this._stateMachine.next()
	}

	enterWorkflow(clientProfile, canEmailForms = true){

		let workflowStateItem = new TraumaWorkflowStateItem()

		workflowStateItem.workflowId = this.id
		workflowStateItem.clientId = clientProfile.id
		workflowStateItem.clientName = clientProfile.name

		workflowStateItem.docRefsArray = [
			new WorkflowDocRefItem(this.safetyRiskAssessmentId, "Safety Risk Assessment", WorkflowDocumentType.SAFETY_RISK_ASSESSMENT, DbObjectType.FORM_ITEM),
			new WorkflowDocRefItem(this.activitiesOfDailyLivingId, "Activities of Daily Living", WorkflowDocumentType.DAILY_LIVING, DbObjectType.FORM_ITEM),
			new WorkflowDocRefItem(this.briefIntakeFormId, "Brief Intake Form", WorkflowDocumentType.BRIEF_INTAKE_FORM, DbObjectType.FORM_ITEM),
			new WorkflowDocRefItem(this.detailedIntakeFormId, "Detailed Intake Form", WorkflowDocumentType.DETAILED_INTAKE_FORM, DbObjectType.FORM_ITEM),

			new WorkflowDocRefItem(this.crisisManagementPlanId, "Crisis Management Plan", WorkflowDocumentType.CRISIS_MANAGEMENT_PLAN, DbObjectType.LEGAL_DOC),
			new WorkflowDocRefItem(this.clientSafetyPlanId, "Client Safety Plan", WorkflowDocumentType.CLIENT_SAFETY_PLAN, DbObjectType.LEGAL_DOC),
			new WorkflowDocRefItem(this.recoveryGoalsWorksheetId, "Recovery Goals Worksheet", WorkflowDocumentType.RECOVERY_GOALS_WORKSHEET, DbObjectType.FORM_ITEM),
		]

		workflowStateItem.getBriefIntakeDocRef().isEmailed = canEmailForms && this.emailBriefIntakeForm
		workflowStateItem.getDailyLivingDocRef().isEmailed = canEmailForms && this.emailDailyActivitiesForm

		return workflowStateItem
	}

	createWorkflowTag(){
		return new TagItem("IAOTRC Workflow", "This client is a part of the International Association of Trauma Recovery Coaching workflow")
	}

	async onFormAnswered(workflowStateItem, formAnswerItem){
		console.log("TraumaCoachingWorkflowItem.onFormAnswered: workflowStateItem = ", workflowStateItem, ", formAnswerItem = ", formAnswerItem)

		if(formAnswerItem.formId === this.safetyRiskAssessmentId){
			return this.workflowOnSafetyRiskCompleted(workflowStateItem, formAnswerItem)
		} else if(formAnswerItem.formId === this.activitiesOfDailyLivingId){
			return this.workflowOnDailyLivingCompleted(workflowStateItem, formAnswerItem)
		} else {
			await this.onFormCompleted(workflowStateItem, formAnswerItem)
			return false
		}
	}


	async onDocSigned(workflowStateItem, docTemplate){

		let templateDocRef = workflowStateItem.findDocRefById(docTemplate.parentTemplateId)

		templateDocRef.completionStatus = CompletionStatus.COMPLETED
		templateDocRef.documentCompletedTs = docTemplate.createdAt
		// templateDocRef.documentDownloadUrl = docTemplate.file.url
		templateDocRef.documentDownloadUrl = docTemplate.getDocUrl()

		templateDocRef.documentResponseId = docTemplate.id

		return templateDocRef
	}

	async onDocSignatureRequested(workflowStateItem, docTemplate){
		let templateDocRef = workflowStateItem.findDocRefById(docTemplate.parentTemplateId)

		templateDocRef.completionStatus = CompletionStatus.WAITING_FOR_SIGNATURE
		templateDocRef.documentSentTs = new Date().getTime()

		return templateDocRef
	}


	async workflowOnSafetyRiskCompleted(workflowStateItem, safetyRiskResponseForm){
		this.onScoredFormCompleted(workflowStateItem, safetyRiskResponseForm)
		let safetyRiskDocRef = workflowStateItem.getSafetyRiskAssessmentDocRef()
		safetyRiskDocRef.setWaitingOther()
		return await this.syncSafetyRiskStatus(workflowStateItem)
	}

	async workflowOnDailyLivingCompleted(workflowStateItem, dailyLivingResponseForm){
		this.onScoredFormCompleted(workflowStateItem, dailyLivingResponseForm)
		let dailyLivingDocRef = workflowStateItem.getDailyLivingDocRef()
		dailyLivingDocRef.setWaitingOther()
		return await this.syncDailyActivityStatus(workflowStateItem)
	}


	syncOverallStatus(workflowStateItem){
		return workflowStateItem.syncOverallStatus()
	}

	async syncSafetyRiskStatus(workflowStateItem){

		let clientSafetyRiskDocRef = this.findClientSafetyRiskDocRef(workflowStateItem)

		if(clientSafetyRiskDocRef.score >= 40){
			workflowStateItem.setSafetyRiskStatus(WorkflowStateEnum.ESCALATED_CARE)
		} else if(clientSafetyRiskDocRef.score >= 30){
			workflowStateItem.setSafetyRiskStatus(WorkflowStateEnum.FOLLOWUP_30_SAFETY_RISK)
		} else if(clientSafetyRiskDocRef.score >= 20){
			workflowStateItem.setSafetyRiskStatus(WorkflowStateEnum.SAFETY_RISK_COMPLETED_SOME_CONCERNS)
		} else {
			workflowStateItem.setSafetyRiskStatus(WorkflowStateEnum.SAFETY_RISK_COMPLETED)
		}


		return await this.syncOverallStatus(workflowStateItem)
	}

	async syncDailyActivityStatus(workflowStateItem){

		let dailyActivityDocRef = this.findDailyActivitiesDocRef(workflowStateItem)

		workflowStateItem.syncSafetyRiskAndDailyLivingCompletionStatus()

		if(dailyActivityDocRef.score >= 20){
			workflowStateItem.setImpairmentsStatus(WorkflowStateEnum.FOLLOWUP_30_60_90_IMPAIRMENTS)
		} else if(dailyActivityDocRef.score >= 10){
			workflowStateItem.setImpairmentsStatus(WorkflowStateEnum.FOLLOWUP_30_90_IMPAIRMENTS)
		} else {
			workflowStateItem.setImpairmentsStatus(WorkflowStateEnum.FOLLOWUP_90)
		}

		return await this.syncOverallStatus(workflowStateItem)
	}


	findClientSafetyRiskDocRef(workflowStateItem){
		return workflowStateItem.findDocRefById(this.safetyRiskAssessmentId)
	}

	findDailyActivitiesDocRef(workflowStateItem){
		return workflowStateItem.findDocRefById(this.activitiesOfDailyLivingId)
	}


	onScoredFormCompleted(workflowStateItem, formAnswerItem){

		let formDoc = workflowStateItem.findDocRefById(formAnswerItem.formId)

		formDoc.completionStatus = CompletionStatus.COMPLETED
		formDoc.score = formAnswerItem.getScoreResultsOrRecalculate()
		formDoc.scoreTitle = formAnswerItem.getScoreTitle()
		formDoc.documentCompletedTs = formAnswerItem.createdAt
		formDoc.documentResponseId = formAnswerItem.id

		return formDoc
	}

	onFormCompleted(workflowStateItem, formAnswerItem){

		let formDoc = workflowStateItem.findDocRefById(formAnswerItem.formId)

		console.log("TraumaCoachingWorkflowItem.onFormCompleted: workflowStateItem = ", workflowStateItem, ", formAnswerItem = ", formAnswerItem, ", formDoc = ", formDoc)

		// if(formDoc){
		formDoc.completionStatus = CompletionStatus.COMPLETED
		formDoc.documentCompletedTs = formAnswerItem.createdAt
		formDoc.documentResponseId = formAnswerItem.id
		// }
		return formDoc
	}


	setupDocumentIdsFrom(workflowDocuments){
		this.briefIntakeFormId 			= workflowDocuments.briefIntakeForm.id
		this.detailedIntakeFormId 		= workflowDocuments.detailedIntakeForm.id
		this.safetyRiskAssessmentId 	= workflowDocuments.safetyRiskAssessment.id
		this.crisisManagementPlanId 	= workflowDocuments.crisisManagementPlan.id
		this.clientSafetyPlanId 		= workflowDocuments.clientSafetyPlan.id
		this.recoveryGoalsWorksheetId 	= workflowDocuments.recoveryGoalsWorksheet.id
		this.activitiesOfDailyLivingId 	= workflowDocuments.activitiesOfDailyLiving.id
	}

	getEmailReminders(generateLinkFn){
		return []
	}


	setupReminders(generateLinkFn){

		this.setupBriefIntakeReminder(generateLinkFn)
	}

	setupBriefIntakeReminder(generateLinkFn){
		console.log("setupBriefIntakeReminder: this = ", this)
		//this code can be called from client or from server and generateLinkFn will be a different function.

		if(!this.emailBriefIntakeForm && !this.emailDailyActivitiesForm){
			return
		}

		let intakeFormHtml = ""
		let dailyActivitiesHtml = ""

		let formsText = ""


		if(this.emailBriefIntakeForm){
			let intakeFormLink = generateLinkFn("FormQuestionsView", {coachId: this.coachId, formId: this.briefIntakeFormId})
			intakeFormHtml = `<a href="${intakeFormLink}" target="_blank">Click to fill in the Intake Form</a>`
			formsText = "Brief Intake Form"
		}
		if(this.emailDailyActivitiesForm){
			let dailyActivityLink = generateLinkFn("FormQuestionsView", {coachId: this.coachId, formId: this.activitiesOfDailyLivingId})
			dailyActivitiesHtml = `<a href="${dailyActivityLink}" target="_blank">Click to fill in the Activities of Daily Living Worksheet</a>`

			let and = this.emailBriefIntakeForm ? " and " : ""
			formsText += and + "Activities of Daily Living Worksheet"
		}

		let subject = `Your form - {{coach_full_name}}.`
		let body =
			`Hi {{invitee_first_name}},

As the next step, please take a moment to fill in the below ${formsText} before our next session:

${intakeFormHtml}

${dailyActivitiesHtml}

Regards,
{{coach_first_name}}`

		let emailReminder = new EmailReminderItem(subject, body, "Send Initial Forms", new ReminderTriggerItemNow())
		emailReminder.isEditable = false
		emailReminder.isEnabled = true
		this.emailReminders.push(emailReminder)
	}

	getAllStages(){
		return this._getAllStagesInternal()
	}


	_getAllStagesInternal(){
		return [
			new WorkflowStageItem("Initial Protocols", 			{states: [WorkflowStateEnum.INITIAL], stageViewName: "InitialProtocolsWorkflowStateView", tooltip: "Activated when a client schedules one of the selected sessions", emptyView: "InitialProtocolEmptyView"}),
			new WorkflowStageItem("Follow up every 30 days", 	{states: [WorkflowStateEnum.FOLLOWUP_30_SAFETY_RISK, WorkflowStateEnum.FOLLOWUP_30_60_90_IMPAIRMENTS, WorkflowStateEnum.FOLLOWUP_30_90_IMPAIRMENTS], stageViewName: "FollowupsWorkflowStateView", tooltip: "Activated based on the imparements and other outcomes from Safety Risk Assessment"}),
			new WorkflowStageItem("Follow up every 90 days", 	{states: [WorkflowStateEnum.FOLLOWUP_90], stageViewName: "FollowupsWorkflowStateView", tooltip: "Activated based on the imparements and other outcomes from Safety Risk Assessment"}),
			new WorkflowStageItem("Escalated Care Protocol", 	{states: [WorkflowStateEnum.ESCALATED_CARE], stageViewName: "EscalatedCareWorkflowStateView", tooltip: "Activated when the Safety Risk Assessment score is above 40"}),
			new WorkflowStageItem("Archived", 					{states: [WorkflowStateEnum.ESCALATED_CARE_COMPLETED], stageViewName: "ArchivedWorkflowStateView"}),
		]
	}

	getShortTitle(){
		return this.titleShort || this.title || "workflow"
	}

}




export const TraumaCoachingWorkflowItemId = TraumaCoachingWorkflowItemIdInternal
